import {
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
    SessionPhase,
    TranscriptMetadata,
    isJobFinished,
    isJobLive,
    isRecordingStarted,
    resolveSessionPhase,
    toTranscriptMetadataFromDocument,
} from 'src/models/TranscriptMetadata'
import { isInvalidToken, isMockActive, isNoFutureSessions, ViewMode } from 'src/utils/env'
import { isUUID } from 'src/utils/text'
import { TranscriptError } from 'src/models/Error'
import { platformHttpClient, visorHttpClient } from 'src/network'
import { analyticsService } from 'src/services/AnalyticsService'
import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { isDocumentFinished } from 'src/models/TranscriptionDocumentMetadata'

import { useTranscriptError } from './TranscriptErrorContext'

type DerivedMetadataValue = {
    isCurrentJobRecordingStarted: boolean
    isCurrentJobFinished: boolean
    sessionPhase: SessionPhase
    shouldUseInsightsFallback: boolean
    setIsCurrentJobFinished: Dispatch<SetStateAction<boolean>>
    setShouldUseInsightsFallback: Dispatch<SetStateAction<boolean>>
    refreshMetadata: () => Promise<void>
}

export const MetadataContext = createContext<TranscriptMetadata | null | undefined>(undefined)
export const DerivedMetadataContext = createContext<DerivedMetadataValue | null>(null)

export const METADATA_REFRESH_TIMEOUT = 2 * 60 * 1000 //ms
const METADATA_QUERY_KEY = 'transcript-metadata'

type MetadataProviderProps = PropsWithChildren<{
    viewMode: ViewMode | null
    eventId: string
    transcriptionDocumentId: string
}>

type MetadataResponse = {
    metadata: TranscriptMetadata
    isFinished: boolean
    shouldContinueRefreshing: boolean
}

const isIdValid = (viewMode: ViewMode | null, transcriptionDocumentId: string, eventId: string) => {
    if (viewMode === 'document') {
        return !!transcriptionDocumentId && isUUID(transcriptionDocumentId)
    }
    if (viewMode === 'job') {
        return !!eventId && isUUID(eventId)
    }

    return false
}

const fetchMetadata = async (viewMode: ViewMode | null, transcriptionDocumentId: string, eventId: string): Promise<MetadataResponse> => {
    if (viewMode === 'document') {
        const documentMetadata = await visorHttpClient.getMetadata(transcriptionDocumentId)
        if (documentMetadata.transcriptState === 'error') {
            throw new Error('Document processing error')
        }

        const isFinished = isDocumentFinished(documentMetadata)

        return {
            metadata: toTranscriptMetadataFromDocument(documentMetadata),
            isFinished,
            shouldContinueRefreshing: !isFinished,
        }
    }

    if (viewMode === 'job') {
        const transcriptMetadata = await platformHttpClient.getTranscriptMetadata(eventId)
        const isFinished = isJobFinished(transcriptMetadata)
        const shouldContinueRefreshing = isJobLive(transcriptMetadata) && !isFinished

        return {
            metadata: transcriptMetadata,
            isFinished,
            shouldContinueRefreshing,
        }
    }

    // Unreachable when callers respect `isQueryEnabled` (which disables the query
    // for null `viewMode`); guarded here to satisfy the return type.
    throw new Error('Unsupported viewMode')
}

export const MetadataProvider = (props: MetadataProviderProps) => {
    const { viewMode, transcriptionDocumentId, eventId, children } = props

    // Set once on first successful fetch per input key and never replaced thereafter. Downstream
    // effects (e.g. `TranscriptProviderNew`) list this in their dep arrays; a mid-session
    // identity change would re-run those effects and recreate the WS client.
    const [metadata, setMetadata] = useState<TranscriptMetadata | null>(null)
    const [isCurrentJobFinished, setIsCurrentJobFinished] = useState(false)
    const [isCurrentJobRecordingStarted, setIsCurrentJobRecordingStarted] = useState(false)
    const [sessionPhase, setSessionPhase] = useState<SessionPhase>('before_session_started')
    const [shouldUseInsightsFallback, setShouldUseInsightsFallback] = useState(false)

    const { setTranscriptError, transcriptErrorHandler } = useTranscriptError()

    const initializedMetadataInputKeyRef = useRef<string | null>(null)

    const hasValidInputs = isMockActive() || isIdValid(viewMode, transcriptionDocumentId, eventId)
    const isQueryEnabled = viewMode !== null && hasValidInputs
    const metadataInputKey = `${viewMode}:${eventId}:${transcriptionDocumentId}`

    const queryKey = useMemo(
        () => [METADATA_QUERY_KEY, viewMode, eventId, transcriptionDocumentId] as const,
        [viewMode, eventId, transcriptionDocumentId],
    )

    const { data, error, isError } = useQuery<MetadataResponse>({
        queryKey,
        queryFn: () => fetchMetadata(viewMode, transcriptionDocumentId, eventId),
        enabled: isQueryEnabled,
        refetchInterval: (query) => (query.state.data?.shouldContinueRefreshing ? METADATA_REFRESH_TIMEOUT : false),
        refetchOnWindowFocus: false,
        // Retries are driven by `refetchInterval` instead of react-query's default
        // immediate backoff: post-initial-load failures should wait for the next
        // scheduled poll and not surface to the user in the meantime
        retry: false,
        staleTime: Infinity,
    })

    // Surfaces validation errors without hitting the network. `isQueryEnabled` already
    // blocks the fetch for these inputs; this effect is the user-visible counterpart.
    useEffect(() => {
        if (viewMode === null) {
            setTranscriptError(TranscriptError.INVALID_LINK)

            return
        }

        if (!hasValidInputs) {
            setTranscriptError(
                isInvalidToken()
                    ? TranscriptError.INVALID_TOKEN
                    : isNoFutureSessions()
                      ? TranscriptError.NO_FUTURE_SESSIONS
                      : TranscriptError.INVALID_LINK,
            )
        }
    }, [viewMode, hasValidInputs, setTranscriptError])

    // `metadata` initialization happens once per input key; derived booleans still
    // sync on every successful poll because the server is canonical for them.
    useEffect(() => {
        if (!data) {
            return
        }

        const isFirstSuccessForInputKey = initializedMetadataInputKeyRef.current !== metadataInputKey

        if (isFirstSuccessForInputKey) {
            initializedMetadataInputKeyRef.current = metadataInputKey
            // Analytics mandatory fields must be registered before any consumer observes
            // `metadata`: consumers emit events (e.g. page visit) synchronously on the
            // first render where metadata is present, and those events read the fields.
            analyticsService.setMandatoryEventFields(data.metadata)
            setMetadata(data.metadata)
        } else {
            // One targeted exception to the immutability rule: patch recording.startedAt
            // when it transitions from null → real value so ferret-web can show absolute
            // timestamps for live jobs opened before recording starts. Fires at most once.
            setMetadata((prev) => {
                const prevRecording = prev?.sessionState?.recording
                const freshRecordingStartedAt = data.metadata.sessionState?.recording?.startedAt

                const recordingStartedAtJustBecameAvailable = prev && !prevRecording?.startedAt && freshRecordingStartedAt

                if (!recordingStartedAtJustBecameAvailable) {
                    return prev
                }

                return {
                    ...prev,
                    sessionState: {
                        ...prev.sessionState,
                        recording: {
                            status: prevRecording?.status ?? null,
                            stopReason: prevRecording?.stopReason ?? null,
                            startedAt: freshRecordingStartedAt,
                        },
                    },
                }
            })
        }

        // These booleans are also writable via `useSetIsJobFinished` /
        // `useSetShouldUseInsightsFallback`. A consumer-written value will be overwritten
        // by the next successful poll that returns fresh server truth — intentional.
        setIsCurrentJobFinished(data.isFinished)
        setSessionPhase(resolveSessionPhase(data.metadata))
        setIsCurrentJobRecordingStarted(isRecordingStarted(data.metadata))
    }, [data, metadataInputKey])

    // Only pre-initialization errors are user-facing. Once `metadata` is populated,
    // later errors are treated as transient: the query keeps the previous successful
    // data and `refetchInterval` will retry at the next scheduled tick.
    useEffect(() => {
        if (!isError || initializedMetadataInputKeyRef.current === metadataInputKey) {
            return
        }

        transcriptErrorHandler(error)
    }, [isError, error, transcriptErrorHandler, metadataInputKey])

    const queryClient = useQueryClient()

    const refreshMetadata = useCallback(async () => {
        await queryClient.refetchQueries({ queryKey })
    }, [queryClient, queryKey])

    const derivedMetadataValue = useShallowMemo<DerivedMetadataValue>({
        isCurrentJobRecordingStarted,
        isCurrentJobFinished,
        sessionPhase,
        shouldUseInsightsFallback,
        setIsCurrentJobFinished,
        setShouldUseInsightsFallback,
        refreshMetadata,
    })

    return (
        <MetadataContext.Provider value={metadata}>
            <DerivedMetadataContext.Provider value={derivedMetadataValue}>{children}</DerivedMetadataContext.Provider>
        </MetadataContext.Provider>
    )
}

export const useTranscriptMetadata = () => {
    const value = useContext(MetadataContext)

    if (value === undefined) {
        throw new Error('You have forgot to use <MetadataContext.Provider />, shame on you.')
    }

    return value
}

export const useIsRecordingStarted = () => {
    return useDerivedMetadataContext().isCurrentJobRecordingStarted
}

export const useSessionPhase = () => {
    return useDerivedMetadataContext().sessionPhase
}

export const useIsJobFinished = () => {
    return useDerivedMetadataContext().isCurrentJobFinished
}

export const useSetIsJobFinished = () => {
    return useDerivedMetadataContext().setIsCurrentJobFinished
}

export const useShouldUseInsightsFallback = () => {
    return useDerivedMetadataContext().shouldUseInsightsFallback
}

export const useSetShouldUseInsightsFallback = () => {
    return useDerivedMetadataContext().setShouldUseInsightsFallback
}

// TODO(LG-1762): callable metadata refresh, currently only used to react to the 4200 "session ended" WS close code.
export const useRefreshMetadata = () => {
    return useDerivedMetadataContext().refreshMetadata
}

const useDerivedMetadataContext = () => {
    const contextValue = useContext(DerivedMetadataContext)

    if (contextValue === null) {
        throw new Error('You have forgot to use <DerivedMetadataContext.Provider />, shame on you.')
    }

    return contextValue
}
